import { useCallback, useEffect, useMemo, useState } from 'react';
import { WbsLabelValueSearchResult } from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';

import useProgramLabelSearch from 'modules/timesheet-labels/hooks/useProgramLabelSearch';
import { DependantLabels } from '../schema';
import { CPT_LABEL_PAGE_COUNT } from '../constants';

type UseProgramsChipData = {
  options: WbsLabelValueSearchResult[];
  handleSearch: (query: string) => void;
  label?: TimesheetLabel | null;
  loading: boolean;
  hasMore: boolean;
};

export const usePrograms = (
  dependentLabels: DependantLabels
): UseProgramsChipData => {
  const [options, setOptions] = useState<WbsLabelValueSearchResult[]>([]);
  const [lastSearch, setLastSearch] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);

  const { searchHandler, data } = useProgramLabelSearch();

  const loading = useMemo(() => Boolean(!data), [data]);

  const handleSearch = useCallback(
    (queryText: string) => {
      if (lastSearch !== queryText) {
        searchHandler({
          query: queryText || undefined
        });
      }
      setLastSearch(queryText);
    },
    [searchHandler, lastSearch, setLastSearch]
  );

  useEffect(() => {
    if (loading) {
      setOptions([]);
      setHasMore(false);
    } else if (data) {
      const programs = data?.wbsLabelValueSearch?.programs
        .items as WbsLabelValueSearchResult[];
      setOptions(programs);
      setHasMore(programs?.length === CPT_LABEL_PAGE_COUNT);
    }
  }, [data, loading]);
  return useMemo(
    () => ({
      options,
      label: dependentLabels?.program?.label,
      handleSearch,
      loading,
      hasMore
    }),
    [options, dependentLabels?.program?.label, handleSearch, loading, hasMore]
  );
};

export default usePrograms;
